import type { NormalizedRecordSearch } from '@ltvco/refresh-lib/utils';

/**
 * Extracts a search query string from normalized first record search data.
 * @param data - NormalizedRecordSearch stored in INITIAL_SEARCH_KEY
 * @returns A search query string or null if the data cannot be converted
 */
export function extractSearchValueFromFirstRecord(
  data: NormalizedRecordSearch | null
): string | null {
  if (!data) return null;

  const { type } = data;

  if (type === 'person' || type === 'obituary' || type === 'ancestry') {
    const fullName = [data.firstName, data.middleName, data.lastName]
      .filter(Boolean)
      .join(' ');
    return fullName.trim() || null;
  }

  if (type === 'phone' && data.phoneNumber) {
    let phoneNumber = data.phoneNumber.replace(/\D/g, '');
    if (phoneNumber.length === 11 && phoneNumber.startsWith('1')) {
      phoneNumber = phoneNumber.substring(1);
    }
    return phoneNumber;
  }

  if (type === 'email' && data.email) {
    return data.email;
  }

  if (type === 'property') {
    const parts = [data.address, data.city, data.state, data.zipCode].filter(
      Boolean
    );
    return parts.length > 0 ? parts.join(', ') : null;
  }

  if (type === 'vehicle') {
    if (data.vin) return data.vin;
    if (data.licensePlate && data.state) {
      return `${data.licensePlate} ${data.state}`;
    }
    if (data.year && data.make && data.model) {
      return `${data.year} ${data.make} ${data.model}`;
    }
  }

  if (type === 'username' && data.username) {
    return data.username;
  }

  if (type === 'contact' && data.name) {
    return data.name;
  }

  if (type === 'unclaimed_money') {
    const fullName = [data.firstName, data.lastName].filter(Boolean).join(' ');
    return fullName.trim() || null;
  }

  if (type === 'recalls') {
    const queryData = data.raw?.queryData as Record<string, string> | undefined;
    if (!queryData) return null;
    const qType = queryData.type;
    if (qType === 'vin' && queryData.vin) return queryData.vin;
    if (qType === 'licensePlate' && queryData.licensePlate) {
      return queryData.state
        ? `${queryData.licensePlate} ${queryData.state}`
        : queryData.licensePlate;
    }
    if (
      qType === 'ymm' &&
      queryData.year &&
      queryData.make &&
      queryData.model
    ) {
      return `${queryData.year} ${queryData.make} ${queryData.model}`;
    }
    if ((qType === 'name' || qType === 'number') && queryData.value) {
      return queryData.value;
    }
    return null;
  }

  return null;
}
